import { getPublicKey } from "nostr-tools/pure";
import { decrypt, encrypt, getConversationKey } from "nostr-tools/nip44";
import { bytesToHex, hexToBytes } from "@noble/hashes/utils.js";

/**
 * The keyring (PLAN.md 4.16 K4): the member's earlier private keys, kept on the server as one
 * NIP-44 payload encrypted to the member's current key, like the address book. It lets the member
 * open capsules addressed to an earlier key without typing that key's phrase. The server stores it
 * and never reads it. An earlier key in it only opens; the server refuses anything it would sign.
 */

export const KEYRING_VERSION = 1;
export const MAX_KEYRING_KEYS = 40;
/**
 * The server's limit on the stored payload (PLAN.md 4.18 step 6): a keyring of MAX_KEYRING_KEYS
 * entries is about 9 700 characters after NIP-44 padding and base64; the test checks it fits.
 */
export const MAX_KEYRING_PAYLOAD_CHARS = 12_000;

export type KeyringEntry = { pubkey: string; secretKey: Uint8Array; added_at: number };
type StoredKeyring = { v: 1; keys: { pubkey: string; secret: string; added_at: number }[] };

export class KeyringError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "KeyringError";
  }
}

function normalize(input: unknown): KeyringEntry[] {
  const raw = input as Partial<StoredKeyring> | null;
  if (!raw || typeof raw !== "object" || raw.v !== KEYRING_VERSION || !Array.isArray(raw.keys)) throw new KeyringError("not a keyring v1");
  if (raw.keys.length > MAX_KEYRING_KEYS) throw new KeyringError(`at most ${MAX_KEYRING_KEYS} keys`);
  const seen = new Set<string>();
  return raw.keys.map((k) => {
    if (!k || typeof k.secret !== "string" || !/^[0-9a-f]{64}$/.test(k.secret)) throw new KeyringError("bad secret key");
    const secretKey = hexToBytes(k.secret);
    const pubkey = getPublicKey(secretKey);
    if (k.pubkey !== pubkey) throw new KeyringError("a keyring entry does not match its key");
    if (seen.has(pubkey)) throw new KeyringError("a key appears twice");
    seen.add(pubkey);
    return { pubkey, secretKey, added_at: Number.isSafeInteger(k.added_at) ? k.added_at : 0 };
  });
}

/** Adds keys (by secret), skipping ones already present and the current key itself. */
export function addToKeyring(entries: KeyringEntry[], secretKeys: Uint8Array[], currentPubkey: string, now = Math.floor(Date.now() / 1000)): KeyringEntry[] {
  const out = [...entries];
  for (const secretKey of secretKeys) {
    const pubkey = getPublicKey(secretKey);
    if (pubkey === currentPubkey || out.some((e) => e.pubkey === pubkey)) continue;
    out.push({ pubkey, secretKey, added_at: now });
  }
  if (out.length > MAX_KEYRING_KEYS) throw new KeyringError(`at most ${MAX_KEYRING_KEYS} keys`);
  return out;
}

/** Encrypts the keyring to the current key (NIP-44 v2, the key's conversation key with itself). */
export function sealKeyring(entries: KeyringEntry[], secretKey: Uint8Array): string {
  const stored: StoredKeyring = { v: 1, keys: entries.map((e) => ({ pubkey: e.pubkey, secret: bytesToHex(e.secretKey), added_at: e.added_at })) };
  normalize(stored);
  return encrypt(JSON.stringify(stored), getConversationKey(secretKey, getPublicKey(secretKey)));
}

export function openKeyring(payload: string, secretKey: Uint8Array): KeyringEntry[] {
  let json: string;
  try {
    json = decrypt(payload, getConversationKey(secretKey, getPublicKey(secretKey)));
  } catch {
    throw new KeyringError("the keyring cannot be opened with this key");
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(json);
  } catch {
    throw new KeyringError("the keyring is damaged");
  }
  return normalize(parsed);
}
